import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, Check, Checks, Funnel } from "@phosphor-icons/react";
import api, { apiErr } from "../api";

const CATS = [
  ["", "All"], ["rival_movement", "Rivals"], ["rival_achievement", "Rival Feats"],
  ["daily", "Daily"], ["rank_promotion", "Ranks"], ["achievement", "Achievements"],
  ["crew", "Crew"], ["recovery", "Recovery"], ["season", "Season"], ["system", "System"],
];

export default function Notifications() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [cat, setCat] = useState("");
  const [offset, setOffset] = useState(0);
  const limit = 20;

  const load = () => {
    const params = { limit, offset };
    if (cat) params.category = cat;
    api.get("/notifications", { params }).then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  };
  useEffect(() => { load(); }, [cat, offset]);

  const readOne = async (n) => {
    if (!n.read) { try { await api.post(`/notifications/${n.id}/read`); } catch {} }
    load();
    if (n.route && n.route.startsWith("/")) nav(n.route);
  };
  const readAll = async () => {
    try { await api.post("/notifications/read-all"); toast.success("All caught up"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-5" data-testid="notifications-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bell size={26} weight="duotone" className="text-ff-gold" />
          <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Notifications</h1>
          {data?.unread > 0 && <span className="rounded-full bg-ff-gold px-2 py-0.5 text-xs font-bold text-ff-bg" data-testid="notif-unread-count">{data.unread}</span>}
        </div>
        <button onClick={readAll} data-testid="notif-read-all" className="flex items-center gap-1 rounded-sm border border-ff-brass/30 px-3 py-1.5 text-sm text-ff-muted hover:text-ff-gold"><Checks size={16} /> Mark all read</button>
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid="notif-filters">
        {CATS.map(([v, label]) => (
          <button key={v || "all"} onClick={() => { setCat(v); setOffset(0); }} data-testid={`notif-filter-${v || "all"}`}
            className={`rounded-full px-3 py-1 text-xs transition-colors ${cat === v ? "bg-ff-gold text-ff-bg" : "border border-ff-brass/25 text-ff-muted hover:text-ff-parchment"}`}>
            {label}
          </button>
        ))}
      </div>

      {!data ? (
        <div className="py-16 text-center text-ff-muted">Loading…</div>
      ) : data.items.length === 0 ? (
        <div className="ff-panel rounded-md p-10 text-center text-ff-muted" data-testid="notif-empty">No notifications here yet.</div>
      ) : (
        <div className="space-y-2" data-testid="notif-list">
          {data.items.map((n) => (
            <button key={n.id} onClick={() => readOne(n)} data-testid={`notif-${n.id}`}
              className={`ff-panel flex w-full items-start gap-3 rounded-md p-3.5 text-left transition-colors hover:border-ff-gold/40 ${n.read ? "opacity-60" : ""}`}>
              <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${n.read ? "bg-ff-muted/40" : "bg-ff-gold"}`} />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-ff-parchment">{n.title}</span>
                  <span className="text-[10px] uppercase tracking-widest text-ff-brass">{n.category.replace("_", " ")}</span>
                </div>
                <div className="text-sm text-ff-muted">{n.message}</div>
                <div className="mt-0.5 text-[10px] text-ff-muted/70">{new Date(n.created_at).toLocaleString()}</div>
              </div>
            </button>
          ))}
        </div>
      )}

      {data && data.total > limit && (
        <div className="flex items-center justify-between text-sm">
          <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))} data-testid="notif-prev" className="rounded-sm border border-ff-brass/30 px-3 py-1.5 disabled:opacity-40">Previous</button>
          <span className="text-ff-muted">{offset + 1}–{Math.min(offset + limit, data.total)} of {data.total}</span>
          <button disabled={offset + limit >= data.total} onClick={() => setOffset(offset + limit)} data-testid="notif-next" className="rounded-sm border border-ff-brass/30 px-3 py-1.5 disabled:opacity-40">Next</button>
        </div>
      )}
    </div>
  );
}
